import { useState, useEffect, useRef } from 'react';

declare global {
    interface Window {
        _cloudAmp?: {
            populateForm?: (form: HTMLElement, fieldName?: string) => void;
        };
    }
}

const STORAGE_KEY = 'cloudamp__data__c';
const INITIAL_DELAY_MS = 5000;
const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 10;

/**
 * Tells cloudamp's ctk.js (already loaded in memory) to populate the given input
 * using its internally cached attribution data. This is needed on SPA navigations
 * where ctk.js has already loaded but hasn't scanned the newly mounted form.
 */
function tryCloudampPopulate(input: HTMLInputElement) {
    const form = input.closest('form');
    if (form && typeof window._cloudAmp?.populateForm === 'function') {
        window._cloudAmp.populateForm(form);
    }
}

/**
 * Use on secondary forms in a page that do NOT have a cloudamp hidden input.
 * Reads the cloudamp data from localStorage after a delay and returns it as a string.
 */
export function useCloudampData() {
    const [cloudampData, setCloudampData] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setCloudampData(localStorage.getItem(STORAGE_KEY) || '');
        }, INITIAL_DELAY_MS);

        return () => clearTimeout(timer);
    }, []);

    return cloudampData;
}

/**
 * Use on the main form in a page that HAS a cloudamp hidden input.
 * Returns a ref to attach to the hidden input.
 *
 * Strategy:
 * 1. Immediately pre-fill from localStorage so the form always has a value even
 *    if cloudamp is slow or blocked (cross-page SPA navigation fallback).
 * 2. After INITIAL_DELAY_MS, call window._cloudAmp.populateForm() so cloudamp
 *    fills the input using its internally stored attribution data. This handles
 *    SPA navigations where ctk.js is already loaded but hasn't scanned the new form.
 * 3. Poll until cloudamp populates the input, then persist the value back to
 *    localStorage for subsequent navigations.
 */
export function useCloudampDataRef() {
    const cloudampDataRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && cloudampDataRef.current && !cloudampDataRef.current.value) {
            cloudampDataRef.current.value = stored;
        }

        const baseline = stored || '';

        let interval: ReturnType<typeof setInterval>;

        const initialTimer = setTimeout(() => {
            if (cloudampDataRef.current) {
                tryCloudampPopulate(cloudampDataRef.current);
            }

            let attempts = 0;

            interval = setInterval(() => {
                const value = cloudampDataRef.current?.value;

                if (value && value !== baseline) {
                    localStorage.setItem(STORAGE_KEY, value);
                    clearInterval(interval);
                } else if (++attempts >= MAX_POLL_ATTEMPTS) {
                    clearInterval(interval);
                }
            }, POLL_INTERVAL_MS);
        }, INITIAL_DELAY_MS);

        return () => {
            clearTimeout(initialTimer);
            clearInterval(interval);
        };
    }, []);

    return cloudampDataRef;
}

export default useCloudampData;
